/**
 * Constantes del modulo CRM (capa de presentacion).
 * Los codigos coinciden con el backend (UPPER_SNAKE); aqui viven las
 * etiquetas legibles y la configuracion de UI.
 */

import { ESTADO_OPTIONS } from "./territories";
// ===== Estados del Lead =====
export const LEAD_STATUS = {
  PENDING_CONTACT: "PENDING_CONTACT",
  VOICEMAIL: "VOICEMAIL",
  CONTACTED: "CONTACTED",
  INFO_SENT: "INFO_SENT",
  IN_NEGOTIATION: "IN_NEGOTIATION",
  PROPOSAL_SENT: "PROPOSAL_SENT",
  AWAITING_RESPONSE: "AWAITING_RESPONSE",
  APPOINTMENT_SET: "APPOINTMENT_SET",
  CURRENT_CLIENT: "CURRENT_CLIENT",
  NOT_INTERESTED: "NOT_INTERESTED",
  NO_RESPONSE: "NO_RESPONSE",
  OUT_OF_SERVICE: "OUT_OF_SERVICE",
  CALL_BACK: "CALL_BACK",
  NO_INFO: "NO_INFO",
};

export const LEAD_STATUS_LABELS = {
  PENDING_CONTACT: "Nuevo",
  NO_INFO: "Nuevo",
  CONTACTED: "Contactando",
  VOICEMAIL: "Contactando",
  CALL_BACK: "Contactando",
  INFO_SENT: "Contactando",
  IN_NEGOTIATION: "Calificado",
  APPOINTMENT_SET: "Demo",
  PROPOSAL_SENT: "Cierre",
  AWAITING_RESPONSE: "Cierre",
  CURRENT_CLIENT: "Ganado",
  NOT_INTERESTED: "Perdido",
  NO_RESPONSE: "Perdido",
  OUT_OF_SERVICE: "Perdido",
};

// Color por etapa del proceso (los estados legacy heredan el de su etapa)
export const LEAD_STATUS_COLORS = {
  PENDING_CONTACT: "#64748b",
  NO_INFO: "#64748b",
  CONTACTED: "#2563eb",
  VOICEMAIL: "#2563eb",
  CALL_BACK: "#2563eb",
  INFO_SENT: "#2563eb",
  IN_NEGOTIATION: "#0d9488",
  APPOINTMENT_SET: "#7c3aed",
  PROPOSAL_SENT: "#d97706",
  AWAITING_RESPONSE: "#d97706",
  CURRENT_CLIENT: "#16a34a",
  NOT_INTERESTED: "#ef4444",
  NO_RESPONSE: "#ef4444",
  OUT_OF_SERVICE: "#ef4444",
};

/** Estados que el usuario puede elegir (1:1 con el proceso de venta). */
export const SALES_PROCESS_STATUSES = [
  LEAD_STATUS.PENDING_CONTACT,
  LEAD_STATUS.CONTACTED,
  LEAD_STATUS.IN_NEGOTIATION,
  LEAD_STATUS.APPOINTMENT_SET,
  LEAD_STATUS.PROPOSAL_SENT,
  LEAD_STATUS.CURRENT_CLIENT,
  LEAD_STATUS.NOT_INTERESTED,
];

const CANONICAL_BY_STATUS = {
  PENDING_CONTACT: LEAD_STATUS.PENDING_CONTACT,
  NO_INFO: LEAD_STATUS.PENDING_CONTACT,
  CONTACTED: LEAD_STATUS.CONTACTED,
  VOICEMAIL: LEAD_STATUS.CONTACTED,
  CALL_BACK: LEAD_STATUS.CONTACTED,
  INFO_SENT: LEAD_STATUS.CONTACTED,
  IN_NEGOTIATION: LEAD_STATUS.IN_NEGOTIATION,
  APPOINTMENT_SET: LEAD_STATUS.APPOINTMENT_SET,
  PROPOSAL_SENT: LEAD_STATUS.PROPOSAL_SENT,
  AWAITING_RESPONSE: LEAD_STATUS.PROPOSAL_SENT,
  CURRENT_CLIENT: LEAD_STATUS.CURRENT_CLIENT,
  NOT_INTERESTED: LEAD_STATUS.NOT_INTERESTED,
  NO_RESPONSE: LEAD_STATUS.NOT_INTERESTED,
  OUT_OF_SERVICE: LEAD_STATUS.NOT_INTERESTED,
};

/** Colapsa estados legacy al código canónico del proceso simplificado. */
export const canonicalLeadStatus = (status) =>
  CANONICAL_BY_STATUS[status] || LEAD_STATUS.PENDING_CONTACT;

export const LEAD_STATUS_OPTIONS = SALES_PROCESS_STATUSES.map((value) => ({
  value,
  label: LEAD_STATUS_LABELS[value],
}));

/** Estados que cierran el lead (sin proximo seguimiento) */
export const CLOSED_LEAD_STATUSES = [
  LEAD_STATUS.NOT_INTERESTED,
  LEAD_STATUS.NO_RESPONSE,
  LEAD_STATUS.OUT_OF_SERVICE,
];

export const isClosedLeadStatus = (status) => CLOSED_LEAD_STATUSES.includes(status);

// ===== Medios de contacto =====
export const CONTACT_TYPE_LABELS = {
  EMAIL: "Correo",
  PHONE: "Teléfono",
  MOBILE: "Celular",
  WHATSAPP: "WhatsApp",
  OTHER: "Otro",
};
export const CONTACT_TYPE_OPTIONS = Object.keys(CONTACT_TYPE_LABELS).map((value) => ({
  value,
  label: CONTACT_TYPE_LABELS[value],
}));

// ===== Origen =====
export const LEAD_SOURCE_LABELS = {
  TELEMARKETING: "Telemarketing",
  FACEBOOK: "Facebook",
  GOOGLE: "Google",
  MARKETING: "Marketing",
  RECOMMENDATION: "Recomendación",
  PROBOT: "ProBot (legacy)",
  TRAZIA: "Trazia",
};
export const LEAD_SOURCE_OPTIONS = Object.keys(LEAD_SOURCE_LABELS)
  .filter((value) => value !== "PROBOT" && value !== "TRAZIA")
  .map((value) => ({
    value,
    label: LEAD_SOURCE_LABELS[value],
  }));

// ===== Tipo de empresa =====
export const COMPANY_TYPE_LABELS = {
  SMALL: "Pequeña",
  MEDIUM: "Mediana",
  PYME: "Mediana",
  LARGE: "Grande",
};
export const COMPANY_TYPE_OPTIONS = ["SMALL", "MEDIUM", "LARGE"].map((value) => ({
  value,
  label: COMPANY_TYPE_LABELS[value],
}));

// ===== Planes comerciales (precios MXN con IVA) =====
export const INTEREST_PLAN_LABELS = {
  STARTER: "Starter",
  PRO: "Pro",
  ENTERPRISE: "Enterprise",
  // Legacy
  MONTHLY: "Mensual (legacy)",
  ANNUAL: "Anual (legacy)",
};

/** Precio mensual. Enterprise = personalizado (null). */
export const PLAN_MONTHLY_PRICES = {
  STARTER: 1999,
  PRO: 2999,
  ENTERPRISE: null,
  MONTHLY: 1999,
  ANNUAL: 1999,
};

/** Valor de pipeline por defecto: 1 mes de suscripcion */
export const DEFAULT_PIPELINE_MONTHS = 1;

/** Plan asumido cuando el lead aún no tiene plan */
export const DEFAULT_INTEREST_PLAN = "STARTER";

export const INTEREST_PLAN_OPTIONS = ["STARTER", "PRO", "ENTERPRISE"].map((value) => {
  const price = PLAN_MONTHLY_PRICES[value];
  const priceLabel =
    price != null ? `$${price.toLocaleString("es-MX")}/mes` : "Personalizado";
  return { value, label: `${INTEREST_PLAN_LABELS[value]} — ${priceLabel}` };
});

// ===== Tipos de seguimiento =====
export const FOLLOWUP_TYPE_LABELS = {
  CALL: "Llamada",
  WHATSAPP: "WhatsApp",
  EMAIL: "Correo",
  VISIT: "Visita",
  PRESENTATION: "Presentación",
};
export const FOLLOWUP_TYPE_OPTIONS = Object.keys(FOLLOWUP_TYPE_LABELS).map((value) => ({
  value,
  label: FOLLOWUP_TYPE_LABELS[value],
}));

// ===== Ciclo de cobro (comisiones) =====
export const BILLING_CYCLE_LABELS = {
  MONTHLY: "Mensual (con residual)",
  ANNUAL: "Anual (comisión de golpe)",
};
export const BILLING_CYCLE_OPTIONS = [
  { value: "MONTHLY", label: BILLING_CYCLE_LABELS.MONTHLY },
  { value: "ANNUAL", label: BILLING_CYCLE_LABELS.ANNUAL },
];

export const MARKETER_BASE_OPTIONS = [
  { value: "initial", label: "Base de la venta (mismo monto que el closer)" },
];

// ===== Cierre =====
export const CLOSE_OUTCOME_LABELS = { WON: "Ganado", LOST: "Perdido", PAUSED: "Perdido" };
export const CLOSE_OUTCOME_OPTIONS = [
  { value: "WON", label: CLOSE_OUTCOME_LABELS.WON },
  { value: "LOST", label: CLOSE_OUTCOME_LABELS.LOST },
];

export const CLOSE_LOSS_REASONS = [
  "Precio",
  "No responde",
  "No es el momento",
  "Eligió a otro",
  "No encaja",
  "Otro",
];
export const CLOSE_LOSS_REASON_OPTIONS = CLOSE_LOSS_REASONS.map((value) => ({ value, label: value }));

/** Meta de actividad del mes por persona (llamadas, contactos, seguimientos). */
export const MONTHLY_ACTIVITY_GOAL = 20;
/** @deprecated ventas; la barra usa MONTHLY_ACTIVITY_GOAL */
export const MONTHLY_WIN_GOAL = MONTHLY_ACTIVITY_GOAL;

// ===== Actividades (timeline) =====
export const ACTIVITY_LABELS = {
  LEAD_CREATED: "Lead creado",
  LEAD_UPDATED: "Lead actualizado",
  EXECUTIVE_CHANGED: "Cambio de ejecutivo",
  STATUS_CHANGED: "Cambio de etapa",
  FOLLOWUP_ADDED: "Seguimiento agregado",
  ATTACHMENT_ADDED: "Adjunto agregado",
  CLOSED: "Cierre",
  CONVERTED: "Conversión a cliente",
};

// ===== Filtros rapidos =====
export const QUICK_FILTERS = [
  { key: "active", label: "Activos" },
  { key: "nuevo", label: "Nuevos" },
  { key: "overdue", label: "Vencidos" },
  { key: "unassigned", label: "Sin asignar" },
  { key: "qualified", label: "Calificados" },
  { key: "won", label: "Ganados" },
];

// ===== Columnas del listado =====
export const LEAD_COLUMNS = [
  { key: "companyName", label: "Empresa" },
  { key: "contactName", label: "Contacto" },
  { key: "assignedTo", label: "Ejecutivo" },
  { key: "status", label: "Etapa" },
  { key: "potentialValue", label: "Valor" },
  { key: "lastContactAt", label: "Último contacto" },
  { key: "nextFollowUpAt", label: "Próx. seguimiento" },
];

export const PAGE_SIZE = 20;

const CORE_FORM = new Set(["companyName", "contactName", "source", "interestPlan", "potentialValue"]);

// ===== Campos del formulario de Lead (sin telefonos/correo: van en contacts) =====
export const LEAD_PRIORITY = {
  NORMAL: "NORMAL",
  URGENT: "URGENT",
};

export const LEAD_PRIORITY_LABELS = {
  NORMAL: "Normal",
  URGENT: "Urgente",
};

export const LEAD_PRIORITY_OPTIONS = [
  { value: "NORMAL", label: "Normal" },
  { value: "URGENT", label: "Urgente" },
];

export const LEAD_FORM_FIELDS = [
  { name: "companyName", label: "Empresa", required: true },
  {
    name: "priority",
    label: "Prioridad",
    type: "select",
    defaultValue: "NORMAL",
    options: LEAD_PRIORITY_OPTIONS,
  },
  { name: "contactName", label: "Nombre del contacto" },
  {
    name: "source",
    label: "Origen",
    type: "select",
    allowNone: true,
    options: LEAD_SOURCE_OPTIONS,
  },
  {
    name: "interestPlan",
    label: "Plan de interés",
    type: "select",
    allowNone: true,
    options: INTEREST_PLAN_OPTIONS,
    compact: true,
  },
  {
    name: "potentialValue",
    label: "Valor personalizado (Enterprise)",
    type: "number",
    min: 0,
    compact: true,
    /** Solo visible / editable cuando el plan no tiene precio fijo */
    showWhen: (form) => form.interestPlan === "ENTERPRISE",
  },
];

export const LEAD_FORM_EXTRA_FIELDS = [
  { name: "position", label: "Puesto", compact: true },
  { name: "website", label: "Sitio web" },
  { name: "address", label: "Dirección" },
  { name: "city", label: "Ciudad", compact: true },
  {
    name: "state",
    label: "Estado",
    type: "select",
    allowNone: true,
    compact: true,
    options: ESTADO_OPTIONS,
  },
  { name: "rfc", label: "RFC", compact: true },
  { name: "businessLine", label: "Giro", compact: true },
  {
    name: "companyType",
    label: "Tipo de empresa",
    type: "select",
    allowNone: true,
    options: COMPANY_TYPE_OPTIONS,
    compact: true,
  },
  { name: "employeesCount", label: "N.º empleados", type: "number", min: 0, compact: true },
  { name: "estimatedCost", label: "Costo estimado", type: "number", min: 0, compact: true },
  { name: "closeProbability", label: "Prob. cierre (%)", type: "number", min: 0, max: 100, compact: true },
];

export const ALL_LEAD_FORM_FIELDS = [...LEAD_FORM_FIELDS, ...LEAD_FORM_EXTRA_FIELDS];
export const isCoreLeadField = (name) => CORE_FORM.has(name);

// ===== Campos del formulario de seguimiento =====
export const FOLLOWUP_FORM_FIELDS = [
  {
    name: "type",
    label: "Tipo",
    type: "select",
    required: true,
    options: FOLLOWUP_TYPE_OPTIONS,
  },
  { name: "notes", label: "Notas (Markdown permitido)", type: "text", rows: 4 },
  {
    name: "nextFollowUpType",
    label: "Tipo próximo seguimiento",
    type: "select",
    allowNone: true,
    options: FOLLOWUP_TYPE_OPTIONS,
    compact: true,
  },
];
